import type { MeasurementTypeRecord } from '../types';

/**
 * The built-in measurement types, carried forward from the previous project so
 * that a restored backup lines up with what was recorded there.
 *
 * `slug` is the stable identity, and — since the sync remediation of
 * 2026-08-24 — `id` is *derived* from it via {@link builtInMeasurementTypeId}
 * rather than a freshly generated UUID. Before that fix, every device that
 * ever opened the app independently minted its own random id for each
 * built-in type; sync's upsert matches by `id`, not `slug`, so those
 * per-device sets never converged into one row and instead all survived as
 * parallel duplicates. A deterministic id makes every device's seed run
 * produce the *same* row, so sync's own content-aware upsert treats it as
 * one row being re-confirmed, not several new ones — the same guarantee the
 * old comment described ("idempotent across a reinstall") but now actually
 * holds across devices too, not just within one.
 *
 * `orderIndex` is spaced by ten so a user-created type can be inserted between
 * two built-ins later (Phase 8) without renumbering the whole list.
 */

/**
 * The stable id for a built-in measurement type, derived from its slug so
 * every device's independent seed run produces the identical row. Exported
 * so the one-time production cleanup (de-duplicating rows seeded before this
 * fix existed) computes "the canonical id for this slug" via this exact
 * function, rather than a second, potentially-drifting copy of the same rule.
 */
export function builtInMeasurementTypeId(slug: string): string {
  return `built_in_${slug}`;
}
export const BUILT_IN_MEASUREMENT_TYPES: readonly Omit<MeasurementTypeRecord, 'id'>[] = [
  { slug: 'bodyweight', name: 'Body weight', unit: 'kg', orderIndex: 10, isBuiltIn: true, isHidden: false },
  { slug: 'body_fat', name: 'Body fat', unit: '%', orderIndex: 20, isBuiltIn: true, isHidden: false },
  { slug: 'neck', name: 'Neck', unit: 'cm', orderIndex: 30, isBuiltIn: true, isHidden: false },
  { slug: 'chest', name: 'Chest', unit: 'cm', orderIndex: 40, isBuiltIn: true, isHidden: false },
  { slug: 'shoulders', name: 'Shoulders', unit: 'cm', orderIndex: 50, isBuiltIn: true, isHidden: false },
  { slug: 'waist', name: 'Waist', unit: 'cm', orderIndex: 60, isBuiltIn: true, isHidden: false },
  { slug: 'hips', name: 'Hips', unit: 'cm', orderIndex: 70, isBuiltIn: true, isHidden: false },
  { slug: 'left_arm', name: 'Left arm', unit: 'cm', orderIndex: 80, isBuiltIn: true, isHidden: false },
  { slug: 'right_arm', name: 'Right arm', unit: 'cm', orderIndex: 90, isBuiltIn: true, isHidden: false },
  { slug: 'left_thigh', name: 'Left thigh', unit: 'cm', orderIndex: 100, isBuiltIn: true, isHidden: false },
  { slug: 'right_thigh', name: 'Right thigh', unit: 'cm', orderIndex: 110, isBuiltIn: true, isHidden: false },
  { slug: 'left_calf', name: 'Left calf', unit: 'cm', orderIndex: 120, isBuiltIn: true, isHidden: false },
  { slug: 'right_calf', name: 'Right calf', unit: 'cm', orderIndex: 130, isBuiltIn: true, isHidden: false },
];
